import React, { useState, useEffect } from 'react';
import { getData, putData } from '../helpers/ApiSettings';

const Orders = () => {
  const [activeTab, setActiveTab] = useState('rentals');
  const [orders, setOrders] = useState([]);
  const [saleOrders, setSaleOrders] = useState([]);
  const [formulaOrders, setFormulaOrders] = useState([]);
  const [workshopReservations, setWorkshopReservations] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [couponDetails, setCouponDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');

  const orderStatuses = {
    pending: 'En attente',
    processing: 'En cours',
    shipped: 'Expédié',
    confirmed: 'Confirmé',
    cancelled: 'Annulé',
    delivered: 'Livré',
    paid: 'Payé',
    pending_payment: 'Paiement en attente',
    returned: 'Retourné',
    completed: 'Terminé',
    failed: 'Échoué',
    refunded: 'Remboursé'
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await getData('/orders?sort[0]=createdAt:desc&populate=*');
      setOrders(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des commandes de location');
      console.error('Error fetching rental orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaleOrders = async () => {
    try {
      setLoading(true);
      const response = await getData('/sale-orders?sort[0]=createdAt:desc&populate=*');
      setSaleOrders(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des commandes de vente');
      console.error('Error fetching sale orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormulaOrders = async () => {
    try {
      setLoading(true);
      const response = await getData('/formula-orders?sort[0]=createdAt:desc&populate=*');
      setFormulaOrders(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des commandes de formules');
      console.error('Error fetching formula orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkshopReservations = async () => {
    try {
      setLoading(true);
      const response = await getData('/workshop-reservations?sort[0]=createdAt:desc&populate[user]=*&populate[workshop]=*&populate[workshop_date]=*');
      setWorkshopReservations(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des réservations d\'atelier');
      console.error('Error fetching workshop reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderItems = async (orderId) => {
    try {
      // setOrderItemsLoading(true);
      const response = await getData(`/order-items?filters[order][id][$eq]=${orderId}&populate=*`);
      
      // Group items by product documentId and aggregate quantities
      const groupedItems = response.data.data.reduce((acc, item) => {
        // Skip items without product (deleted products, etc.)
        if (!item.product || !item.product.documentId) {
          console.warn('Order item without valid product:', item);
          return acc;
        }
        
        const documentId = item.product.documentId;
        
        if (!acc[documentId]) {
          acc[documentId] = {
            ...item,
            quantity: 1,
            product: item.product,
            price: item.price,
            start_date: item.start_date,
            end_date: item.end_date
          };
        } else {
          // Increment quantity for existing product
          acc[documentId].quantity += 1;
        }
        
        return acc;
      }, {});
  
      // Convert the grouped items object back to an array
      const aggregatedItems = Object.values(groupedItems);
      
      setOrderItems(aggregatedItems);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des détails de la commande');
      console.error('Error fetching order items:', err);
    } finally {
      // setOrderItemsLoading(false);
    }
  };

  const fetchCouponDetails = async (couponId) => {
    try {
      if (!couponId) {
        setCouponDetails(null);
        return;
      }
      
      const response = await getData(`/coupons?filters[id][$eq]=${couponId}&populate=*`);
      if (response.data.data && response.data.data.length > 0) {
        setCouponDetails(response.data.data[0]);
      } else {
        setCouponDetails(null);
      }
    } catch (err) {
      console.error('Error fetching coupon details:', err);
      setCouponDetails(null);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      let endpoint, statusField;
      switch(activeTab) {
        case 'rentals':
          endpoint = `/orders/${orderId}`;
          statusField = 'order_status';
          break;
        case 'sales':
          endpoint = `/sale-orders/${orderId}`;
          statusField = 'order_status';
          break;
        case 'formulas':
          endpoint = `/formula-orders/${orderId}`;
          statusField = 'order_status';
          break;
        case 'workshops':
          endpoint = `/workshop-reservations/${orderId}`;
          statusField = 'resa_status';
          break;
        default:
          endpoint = `/orders/${orderId}`;
          statusField = 'order_status';
      }
      
      await putData(endpoint, {
        data: { [statusField]: newStatus }
      });
      handleRefresh(); // Refresh the current tab
      alert('Statut mis à jour avec succès');
    } catch (err) {
      alert('Erreur lors de la mise à jour du statut');
      console.error('Error updating order status:', err);
    }
  };

  useEffect(() => {
    // Fetch all order types on initial load to get proper counts
    fetchOrders();
    fetchSaleOrders();
    fetchFormulaOrders();
    fetchWorkshopReservations();
  }, []);

  useEffect(() => {
    // Only fetch specific tab data when switching tabs (after initial load)
    switch(activeTab) {
      case 'rentals':
        if (orders.length === 0) fetchOrders();
        break;
      case 'sales':
        if (saleOrders.length === 0) fetchSaleOrders();
        break;
      case 'formulas':
        if (formulaOrders.length === 0) fetchFormulaOrders();
        break;
      case 'workshops':
        if (workshopReservations.length === 0) fetchWorkshopReservations();
        break;
      default:
        if (orders.length === 0) fetchOrders();
    }
  }, [activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(dateString));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-warning',
      processing: 'bg-info',
      shipped: 'bg-primary',
      confirmed: 'bg-success',
      cancelled: 'bg-danger'
    };
    return `badge ${statusClasses[status] || 'bg-secondary'}`;
  };

  const getCurrentOrders = () => {
    switch(activeTab) {
      case 'rentals':
        return orders;
      case 'sales':
        return saleOrders;
      case 'formulas':
        return formulaOrders;
      case 'workshops':
        return workshopReservations;
      default:
        return orders;
    }
  };

  const getAvailableStatuses = () => {
    switch(activeTab) {
      case 'rentals':
        return Object.entries(orderStatuses).filter(([key]) => 
          ['pending', 'processing', 'shipped', 'confirmed', 'cancelled'].includes(key)
        );
      case 'sales':
        return Object.entries(orderStatuses).filter(([key]) => 
          ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].includes(key)
        );
      case 'formulas':
        return Object.entries(orderStatuses).filter(([key]) => 
          ['pending_payment', 'paid', 'delivered', 'returned', 'cancelled'].includes(key)
        );
      case 'workshops':
        return Object.entries(orderStatuses).filter(([key]) => 
          ['pending_payment', 'confirmed', 'cancelled', 'completed'].includes(key)
        );
      default:
        return Object.entries(orderStatuses);
    }
  };

  const currentOrders = getCurrentOrders();
  const filteredOrders = selectedStatus === 'all'
    ? currentOrders
    : currentOrders.filter(order => {
        const status = order.order_status || order.status || order.resa_status;
        return status === selectedStatus;
      });

  // Helper function to calculate subtotal before coupon
  const calculateSubtotal = (orderItems, deliveryPrice) => {
    const itemsTotal = orderItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    return itemsTotal + deliveryPrice;
  };

  // Helper function to calculate coupon discount
  const calculateCouponDiscount = (subtotal, order, couponDetails) => {
    if (!order.coupon) return 0;
    
    // If we have the stored discount amount, use it (most reliable)
    if (order.coupon.discount && !isNaN(order.coupon.discount)) {
      return parseFloat(order.coupon.discount);
    }
    
    // If we have coupon details, calculate it
    if (couponDetails && couponDetails.amount && !isNaN(couponDetails.amount)) {
      if (couponDetails.is_percentage) {
        return (subtotal * parseFloat(couponDetails.amount)) / 100;
      } else {
        return parseFloat(couponDetails.amount);
      }
    }
    
    return 0;
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const handleRefresh = () => {
    switch(activeTab) {
      case 'rentals':
        fetchOrders();
        break;
      case 'sales':
        fetchSaleOrders();
        break;
      case 'formulas':
        fetchFormulaOrders();
        break;
      case 'workshops':
        fetchWorkshopReservations();
        break;
      default:
        fetchOrders();
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Commandes</h2>
        <div className="d-flex gap-2">
          <select
            className="form-select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            {Object.entries(orderStatuses).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            className="btn btn-outline-primary"
          >
            Actualiser
          </button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'rentals' ? 'active' : ''}`}
            onClick={() => setActiveTab('rentals')}
          >
            Locations ({orders.length})
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'sales' ? 'active' : ''}`}
            onClick={() => setActiveTab('sales')}
          >
            Ventes ({saleOrders.length})
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'formulas' ? 'active' : ''}`}
            onClick={() => setActiveTab('formulas')}
          >
            Formules Loisirs ({formulaOrders.length})
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'workshops' ? 'active' : ''}`}
            onClick={() => setActiveTab('workshops')}
          >
            Ateliers ({workshopReservations.length})
          </button>
        </li>
      </ul>

      <div className="card">
        <div className="card-body">
          {filteredOrders.length === 0 ? (
            <p className="text-center text-muted my-5">Aucune commande trouvée</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>N° Commande</th>
                    <th>Date</th>
                    <th>Client</th>
                    {activeTab === 'workshops' ? <th>Atelier</th> : null}
                    {activeTab === 'formulas' ? <th>Formule</th> : null}
                    <th>Total</th>
                    {activeTab !== 'workshops' && <th>Coupon</th>}
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const status = order.order_status || order.status || order.resa_status;
                    const orderNumber = order.order_number || order.orderNumber || order.id;
                    const orderTotal = order.total || order.total_price;
                    
                    return (
                    <tr key={order.id}>
                      <td>#{orderNumber}</td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>
                        {order.user ? (
                          <>
                            {order.user.first_name} {order.user.last_name}<br />
                            <small className="text-muted">{order.user.email}</small>
                          </>
                        ) : order.user_details ? (
                          <>
                            {order.user_details.name || '-'}<br />
                            <small className="text-muted">{order.user_details.email || '-'}</small>
                            <span className="badge bg-secondary ms-1" style={{ fontSize: '0.7em' }}>Invité</span>
                          </>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      {activeTab === 'workshops' && (
                        <td>{order.workshop?.title || '-'}</td>
                      )}
                      {activeTab === 'formulas' && (
                        <td>{order.formula?.name || '-'}</td>
                      )}
                      <td>{formatPrice(orderTotal)}</td>
                      {activeTab !== 'workshops' && (
                        <td>
                          {order.coupon ? (
                            <span className="badge bg-success">
                              {order.coupon.code || 'Oui'}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      )}
                      <td>
                        <span className={getStatusBadgeClass(status)}>
                          {orderStatuses[status] || status}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-info me-2"
                            data-bs-toggle="modal"
                            data-bs-target={`#orderModal-${order.id}`}
                            onClick={() => {
                              if (activeTab === 'rentals') {
                                fetchOrderItems(order.id);
                              }
                              if (order.coupon?.id) {
                                fetchCouponDetails(order.coupon.id);
                              } else {
                                setCouponDetails(null);
                              }
                            }}
                          >
                            Détails
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target={`#statusModal-${order.id}`}
                          >
                            Modifier
                          </button>
                        </div>

                        {/* Order Details Modal */}
                        <div className="modal fade" id={`orderModal-${order.id}`} tabIndex="-1">
                          <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h5 className="modal-title">
                                  {activeTab === 'rentals' && `Commande Location #${orderNumber}`}
                                  {activeTab === 'sales' && `Commande Vente #${orderNumber}`}
                                  {activeTab === 'formulas' && `Commande Formule #${orderNumber}`}
                                  {activeTab === 'workshops' && `Réservation Atelier #${orderNumber}`}
                                </h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                              </div>
                              <div className="modal-body">
                                <div className="row mb-4">
                                  <div className="col-md-6">
                                    <h6>Informations Client</h6>
                                    {order.user ? (
                                      <p>
                                        <strong>Nom:</strong> {order.user.first_name} {order.user.last_name}<br />
                                        <strong>Email:</strong> {order.user.email}<br />
                                        <strong>Téléphone:</strong> {order.user_details?.phone || '-'}
                                      </p>
                                    ) : order.user_details ? (
                                      <p>
                                        <strong>Nom:</strong> {order.user_details.name || '-'}
                                        <span className="badge bg-secondary ms-2">Invité</span><br />
                                        <strong>Email:</strong> {order.user_details.email || '-'}<br />
                                        <strong>Téléphone:</strong> {order.user_details.phone || '-'}
                                      </p>
                                    ) : (
                                      <p className="text-muted">Aucune information client disponible</p>
                                    )}
                                  </div>
                                  {activeTab !== 'workshops' && (
                                  <div className="col-md-6">
                                    <h6>Adresse de livraison</h6>
                                    {order.delivery_method === "home_delivery" ? (
                                      <p>
                                        {order.user_details?.address_1}<br />
                                        {order.user_details?.address_2 && (`${order.user_details?.address_2}<br />`)}
                                        {order.user_details?.postal_code}, {order.user_details?.city}<br />
                                        {order.user_details?.country}
                                      </p>
                                    ):(
                                      <p>
                                        <strong>{order.relay_point?.name}</strong><br />
                                        {order.relay_point?.address}<br />
                                        {order.relay_point?.postalCode}, {order.relay_point?.city}<br />
                                        Tél : {order.relay_point?.phone}<br />
                                      </p>
                                    )}
                                  </div>
                                  )}
                                </div>

                                {/* Coupon Information */}
                                {order.coupon && (
                                  <div className="row mb-4">
                                    <div className="col-12">
                                      <div className="alert alert-success">
                                        <h6 className="mb-2">
                                          <i className="fas fa-ticket-alt me-2"></i>
                                          Bon d'achat utilisé
                                        </h6>
                                        <p className="mb-1">
                                          <strong>Code:</strong> {order.coupon.code}<br />
                                          <strong>Réduction appliquée:</strong> {formatPrice(order.coupon.discount || 0)}
                                        </p>
                                        {couponDetails && (
                                          <p className="mb-1">
                                            <strong>Détails du coupon:</strong><br />
                                            <small className="text-muted">
                                              {couponDetails.is_percentage 
                                                ? `${couponDetails.amount}% de réduction` 
                                                : `${formatPrice(couponDetails.amount)} de réduction fixe`
                                              }
                                              {couponDetails.min_amount > 0 && ` • Minimum ${formatPrice(couponDetails.min_amount)}`}
                                            </small>
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Rental Orders - Show products and dates */}
                                {activeTab === 'rentals' && (
                                <>
                                  <h6>Produits et dates de location</h6>
                                  <table className="table table-sm">
                                    <thead>
                                      <tr>
                                        <th>Produit</th>
                                        <th>Date début</th>
                                        <th>Date fin</th>
                                        <th>Quantité</th>
                                        <th>Prix unitaire</th>
                                        <th>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {orderItems?.map((item, index) => (
                                        <tr key={index}>
                                          <td>{item.product?.titre || 'Produit supprimé'}</td>
                                          <td>
                                            <span className="badge bg-info">
                                              {formatDateOnly(item.start_date)}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="badge bg-warning text-dark">
                                              {formatDateOnly(item.end_date)}
                                            </span>
                                          </td>
                                          <td>{item.quantity}</td>
                                          <td>{formatPrice(item.price)}</td>
                                          <td>{formatPrice(item.quantity * item.price)}</td>
                                        </tr>
                                      ))}
                                      <tr>
                                        <td colSpan="4">
                                          {order.delivery_method === 'home_delivery' ? 'Livraison à domicile' : 'Livraison en point relais'}
                                        </td>
                                        <td></td>
                                        <td>{formatPrice(order.delivery_price)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </>
                                )}
                                
                                {/* Sale Orders - Show items */}
                                {activeTab === 'sales' && (
                                <>
                                  <h6>Articles commandés</h6>
                                  <div className="table-responsive">
                                    <table className="table table-sm">
                                      <thead>
                                        <tr>
                                          <th>Article</th>
                                          <th>Quantité</th>
                                          <th>Prix unitaire</th>
                                          <th>Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {order.items && (() => {
                                          let items = order.items;
                                          if (typeof items === 'string') {
                                            try {
                                              items = JSON.parse(items);
                                            } catch (e) {
                                              console.error('Error parsing items:', e);
                                              return null;
                                            }
                                          }
                                          if (Array.isArray(items)) {
                                            return items.map((item, index) => (
                                              <tr key={index}>
                                                <td>{item.product_title || item.name || item.title || 'Article'}</td>
                                                <td>{item.quantity || 1}</td>
                                                <td>{formatPrice(item.unit_price || item.price || 0)}</td>
                                                <td>{formatPrice(item.total_price || (item.quantity || 1) * (item.unit_price || item.price || 0))}</td>
                                              </tr>
                                            ));
                                          }
                                          return null;
                                        })()}
                                        {order.delivery_price && order.delivery_price > 0 && (
                                          <tr>
                                            <td colSpan="2">
                                              {order.delivery_method === 'home_delivery' ? 'Livraison à domicile' : 'Livraison en point relais'}
                                            </td>
                                            <td></td>
                                            <td>{formatPrice(order.delivery_price)}</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                                )}
                                
                                {/* Formula Orders - Show selected products */}
                                {activeTab === 'formulas' && (
                                <>
                                  <h6>Produits sélectionnés pour la formule</h6>
                                  <div className="table-responsive">
                                    <table className="table table-sm">
                                      <thead>
                                        <tr>
                                          <th>Produit</th>
                                          <th>Quantité</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {order.selected_products?.map((product, index) => {
                                          const productDetails = order.selected_products_details ? 
                                            (typeof order.selected_products_details === 'string' ? 
                                              JSON.parse(order.selected_products_details) : 
                                              order.selected_products_details) : {};
                                          
                                          // Handle quantity safely
                                          let quantity = 1;
                                          const productKey = product.id || product.documentId;
                                          if (productKey && productDetails[productKey]) {
                                            const detailQuantity = productDetails[productKey];
                                            quantity = typeof detailQuantity === 'object' ? detailQuantity.quantity || 1 : detailQuantity || 1;
                                          }
                                          
                                          return (
                                            <tr key={index}>
                                              <td>{product.name || product.title}</td>
                                              <td>{quantity}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  {order.deposit_amount && (
                                    <div className="alert alert-info">
                                      <strong>Caution:</strong> {formatPrice(order.deposit_amount)}
                                    </div>
                                  )}
                                </>
                                )}
                                
                                {/* Workshop Reservations - Show workshop details */}
                                {activeTab === 'workshops' && (
                                <>
                                  <h6>Détails de la réservation</h6>
                                  <div className="row">
                                    <div className="col-md-6">
                                      <div className="mb-3">
                                        <strong>Atelier:</strong> {order.workshop?.title || 'Non spécifié'}<br />
                                        <strong>Prix total:</strong> {formatPrice(order.total_price || 0)}
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div>
                                        <strong>Dates sélectionnées:</strong><br />
                                        {order.workshop_date && order.workshop_date.length > 0 ? (
                                          order.workshop_date.map((dateObj, index) => (
                                            <span key={index} className="badge bg-info me-1 mb-1">
                                              {formatDate(dateObj.date)}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-muted">Aucune date disponible</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </>
                                )}
                                
                                {/* Total Summary Table */}
                                <div className="mt-4">
                                  <table className="table table-sm">
                                  <tfoot>
                                    {activeTab !== 'workshops' && order.coupon && (
                                      <>
                                        <tr>
                                          <td colSpan="3" className="text-end">Sous-total avant réduction</td>
                                          <td>
                                            {activeTab === 'rentals' 
                                              ? formatPrice(calculateSubtotal(orderItems, order.delivery_price || 0))
                                              : formatPrice((order.subtotal || 0) + (order.delivery_price || 0))}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td colSpan="3" className="text-end text-success">
                                            <strong>Réduction ({order.coupon.code})</strong>
                                          </td>
                                          <td className="text-success">
                                            <strong>
                                              -{formatPrice(order.discount_amount || order.coupon.discount || 0)}
                                            </strong>
                                          </td>
                                        </tr>
                                      </>
                                    )}
                                    <tr>
                                      <td colSpan="3" className="text-end"><strong>Total final</strong></td>
                                      <td><strong>{formatPrice(orderTotal)}</strong></td>
                                    </tr>
                                  </tfoot>
                                </table>
                                </div>
                              </div>
                              <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                  Fermer
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Status Update Modal */}
                        <div className="modal fade" id={`statusModal-${order.id}`} tabIndex="-1">
                          <div className="modal-dialog">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h5 className="modal-title">Modifier le statut</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                              </div>
                              <div className="modal-body">
                                <div className="list-group">
                                  {getAvailableStatuses().map(([value, label]) => {
                                    const currentStatus = order.order_status || order.status || order.resa_status;
                                    return (
                                      <button
                                        key={value}
                                        className={`list-group-item list-group-item-action ${currentStatus === value ? 'active' : ''}`}
                                        onClick={() => handleStatusChange(order.documentId, value)}
                                        data-bs-dismiss="modal"
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Orders;